import { useEffect, useMemo, useState } from 'react';
import { usePermissionCatalogue, useSetUserPermissions, useUserPermissions } from '../api/hooks';
import type { CrmUser } from '../api/types';
import { PermissionMatrix, type PermissionState } from '../components/PermissionMatrix';
import { ErrorBanner, Modal } from '../components/ui';

/**
 * Per-user permission exceptions.
 *
 * The admin ticks the *effective* state they want. Anything matching the role
 * is stored as no override at all, so the exception list stays short and
 * "who is unusual?" remains answerable.
 */
export function UserPermissionsModal({ user, onClose }: { user: CrmUser; onClose: () => void }) {
  const { data: catalogue } = usePermissionCatalogue();
  const { data: current, isPending } = useUserPermissions(user.id);
  const save = useSetUserPermissions();

  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!current) return;
    setDraft(Object.fromEntries(current.permissions.map((p) => [p.key, p.effective])));
    setError(null);
  }, [current]);

  const state = useMemo<Record<string, PermissionState>>(() => {
    const out: Record<string, PermissionState> = {};
    for (const p of current?.permissions ?? []) {
      const checked = draft[p.key] ?? p.effective;
      out[p.key] = {
        checked,
        fromRole: p.from_role,
        // Recomputed from the draft, so the badge updates as you click rather
        // than only after saving.
        override: checked === p.from_role ? null : checked ? 'grant' : 'revoke',
      };
    }
    return out;
  }, [current, draft]);

  const changes = useMemo(() => {
    if (!current) return { added: [] as string[], removed: [] as string[] };
    const added: string[] = [];
    const removed: string[] = [];
    for (const p of current.permissions) {
      const checked = draft[p.key] ?? p.effective;
      if (checked === p.effective) continue;
      (checked ? added : removed).push(p.key);
    }
    return { added, removed };
  }, [current, draft]);

  const dirty = changes.added.length > 0 || changes.removed.length > 0;
  const overrideCount = Object.values(state).filter((s) => s.override).length;

  async function submit() {
    setError(null);
    try {
      await save.mutateAsync({ userId: user.id, permissions: draft });
      onClose();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Modal
      title={`Permissions — ${user.full_name}`}
      onClose={onClose}
      width={720}
      footer={
        <>
          <span style={{ marginRight: 'auto', fontSize: 12.5, color: 'var(--muted)' }}>
            {overrideCount === 0
              ? 'Exactly what the role gives'
              : `${overrideCount} exception${overrideCount === 1 ? '' : 's'} to the role`}
          </span>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!dirty || save.isPending}>
            {save.isPending ? <span className="spinner" /> : 'Save permissions'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />

      <div className="banner banner-info">
        <div>
          {user.role ? (
            <>
              <strong>{user.full_name}</strong> is a <strong>{user.role.name}</strong>. Ticked boxes
              marked <em>from role</em> come with that role. Change any box to give this person an
              exception — it applies only to them.
            </>
          ) : (
            <>This user has no role, so every permission here is an explicit exception.</>
          )}
        </div>
      </div>

      {dirty && (
        <div className="banner banner-info" style={{ borderColor: 'var(--warn)', color: 'var(--warn)', background: 'transparent' }}>
          <div>
            {changes.added.length > 0 && <div>Granting: {changes.added.join(', ')}</div>}
            {changes.removed.length > 0 && <div>Revoking: {changes.removed.join(', ')}</div>}
            <div style={{ marginTop: 4, fontSize: 12 }}>
              Takes effect when their session refreshes — within 15 minutes, or immediately on
              re-login.
            </div>
          </div>
        </div>
      )}

      {isPending || !catalogue ? (
        <div className="skeleton" style={{ height: 320 }} />
      ) : (
        <PermissionMatrix
          groups={catalogue.groups}
          state={state}
          showOrigin
          disabled={save.isPending}
          onToggle={(key, next) => setDraft((d) => ({ ...d, [key]: next }))}
          onReset={(key) => {
            const permission = current?.permissions.find((p) => p.key === key);
            if (permission) setDraft((d) => ({ ...d, [key]: permission.from_role }));
          }}
        />
      )}
    </Modal>
  );
}
